'use client'

import * as React from 'react'
import Link from 'next/link'
import * as Dialog from '@radix-ui/react-dialog'
import {
  IconChevronRight,
  IconInfoCircle,
  IconLayoutDashboard,
  IconLogout,
  IconPlus,
  IconReceipt,
  IconServer,
  IconWallet,
  IconX,
} from '@tabler/icons-react'
import {
  getMachineStatusConfig,
  isTransitioningStatus,
} from '@/components/machines/machine-status-badge'
import { getCurrencySymbol } from '@/lib/utils/currency'
import { cn } from '@/lib/utils'
import { AvatarFace, CREDIT_STEPS, creditLevel, type DockUser } from './dock-parts'

interface CommandCenterProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: DockUser
  pathname: string | null
  showWallet: boolean
  walletBalance: string
  walletCurrency: string
  walletReady: boolean
  walletHref: string
  /** Safe mode, decided on the server: every machine is simulated. */
  testMode: boolean
  onSignOut?: () => void | Promise<void>
}

/** One machine as GET /api/machines returns it; only what the preview shows. */
interface FleetMachine {
  id: number
  name: string
  status: string
}

type FleetState =
  | { kind: 'loading' }
  | { kind: 'ready'; machines: FleetMachine[] }
  | { kind: 'error' }

const TILES = [
  { label: 'Dashboard', caption: 'Your overview', href: '/dashboard', icon: IconLayoutDashboard, hue: 'var(--primary)', exact: true },
  { label: 'My Machines', caption: 'Start, stop, connect', href: '/machines', icon: IconServer, hue: 'var(--accent)', exact: false },
  { label: 'Billing', caption: 'Wallet and top-ups', href: '/wallet', icon: IconWallet, hue: 'var(--chart-3)', exact: true },
  { label: 'Transactions', caption: 'Every charge and credit', href: '/wallet/transactions', icon: IconReceipt, hue: 'var(--chart-2)', exact: true },
]

export function CommandCenter({
  open,
  onOpenChange,
  user,
  pathname,
  showWallet,
  walletBalance,
  walletCurrency,
  walletReady,
  walletHref,
  testMode,
  onSignOut,
}: CommandCenterProps) {
  const [fleet, setFleet] = React.useState<FleetState>({ kind: 'loading' })
  const [signingOut, setSigningOut] = React.useState(false)

  // Fresh machine states every time the panel opens.
  React.useEffect(() => {
    if (!open) return
    let cancelled = false
    fetch('/api/machines', { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error(String(response.status)))))
      .then((body: { data?: FleetMachine[] }) => {
        if (cancelled) return
        setFleet({
          kind: 'ready',
          machines: (body.data ?? []).map((m) => ({ id: m.id, name: m.name, status: m.status })),
        })
      })
      .catch(() => {
        if (!cancelled) setFleet({ kind: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [open])

  const level = creditLevel(walletBalance)
  const symbol = getCurrencySymbol(walletCurrency)

  const isActive = (href: string, exact: boolean) =>
    exact ? pathname === href : pathname === href || !!pathname?.startsWith(`${href}/`)

  const handleSignOut = async () => {
    if (!onSignOut) return
    setSigningOut(true)
    try {
      await onSignOut()
      onOpenChange(false)
    } finally {
      setSigningOut(false)
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dock-overlay fixed inset-0 z-[60] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 motion-reduce:animate-none" />
        <Dialog.Content
          id="command-center"
          className={cn(
            'dock-panel fixed inset-y-0 right-0 z-[70] flex w-full max-w-[420px] flex-col overflow-hidden text-foreground outline-none sm:inset-y-3 sm:right-3',
            'data-[state=open]:animate-in data-[state=open]:slide-in-from-right data-[state=open]:duration-500',
            'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=closed]:fade-out-0 data-[state=closed]:duration-300',
            'motion-reduce:animate-none'
          )}
        >
          <span aria-hidden className="dock-panel-grid" />

          {/* Top bar */}
          <div className="relative flex items-center justify-between gap-3 px-5 pb-3 pt-5">
            <div className="flex items-center gap-2.5">
              <span aria-hidden className="dock-led" />
              <Dialog.Title className="dock-section-label !text-foreground">Command center</Dialog.Title>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="hidden rounded-sm px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground ring-1 ring-foreground/15 sm:inline">
                Esc
              </kbd>
              <Dialog.Close className="dock-key" aria-label="Close account panel">
                <IconX className="h-4 w-4" stroke={2} />
              </Dialog.Close>
            </div>
          </div>
          <Dialog.Description className="sr-only">
            Your account, wallet balance, machines and account pages.
          </Dialog.Description>

          <div className="relative flex-1 space-y-4 overflow-y-auto px-5 pb-5">
            {/* Player card */}
            <section className="dock-rise flex items-center gap-4" style={{ '--i': 0 } as React.CSSProperties}>
              <span className="dock-avatar shrink-0 overflow-hidden" style={{ width: 56, height: 56 }}>
                <AvatarFace user={user} size={56} />
              </span>
              <div className="min-w-0">
                <p className="truncate font-heading text-lg font-bold leading-tight">{user.name || 'Your account'}</p>
                {user.email && <p className="truncate text-sm text-muted-foreground">{user.email}</p>}
                <p className="mt-1.5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-primary">
                  <span aria-hidden className="dock-blink h-1.5 w-1.5 rounded-[1px] bg-primary" />
                  Signed in
                </p>
              </div>
            </section>

            {/* Wallet */}
            {showWallet && (
              <section
                aria-labelledby="cc-wallet"
                className="dock-vault dock-rise p-4"
                style={{ '--i': 1 } as React.CSSProperties}
              >
                <span aria-hidden className="dock-vault-sweep" />
                <div className="relative flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p id="cc-wallet" className="dock-section-label">Wallet balance</p>
                    {walletReady ? (
                      <p className="mt-1.5 truncate font-heading text-[2rem] font-bold leading-none tabular-nums">
                        {walletBalance}
                      </p>
                    ) : (
                      <span aria-label="Loading balance" className="mt-2 block h-8 w-32 animate-pulse rounded-md bg-foreground/10" />
                    )}
                  </div>
                  <Link
                    href={walletHref}
                    onClick={() => onOpenChange(false)}
                    className="dock-tile-icon shrink-0"
                    style={{ '--hue': 'var(--primary)' } as React.CSSProperties}
                    aria-label="Open wallet"
                  >
                    <IconWallet className="h-4 w-4" stroke={2} />
                  </Link>
                </div>

                <div className="relative mt-4">
                  <div className="dock-section-label flex items-center justify-between">
                    <span>Credit level</span>
                    <span className="text-primary">
                      {walletReady ? level : 0}/{CREDIT_STEPS.length}
                    </span>
                  </div>
                  <div aria-hidden className="dock-meter-wrap mt-2">
                    <div className="dock-meter">
                      {CREDIT_STEPS.map((step, index) => (
                        <span key={step} data-on={walletReady && index < level} />
                      ))}
                    </div>
                  </div>
                  <div aria-hidden className="mt-1.5 flex justify-between font-mono text-[10px] text-muted-foreground">
                    <span>{symbol}0</span>
                    <span>{symbol}{CREDIT_STEPS[Math.floor(CREDIT_STEPS.length / 2) - 1]}</span>
                    <span>{symbol}{CREDIT_STEPS[CREDIT_STEPS.length - 1]}+</span>
                  </div>
                </div>

                <p className="relative mt-3 text-xs leading-relaxed text-muted-foreground">
                  Running machines are charged from this balance at least hourly and when they stop.
                </p>

                <Link
                  href="/wallet/topup"
                  onClick={() => onOpenChange(false)}
                  className="dock-cta relative mt-4 w-full"
                >
                  <IconPlus className="h-4 w-4" stroke={2.5} />
                  Add credit
                </Link>
              </section>
            )}

            {/* Machine status, straight from the machines list */}
            {fleet.kind !== 'error' && (
              <section
                aria-labelledby="cc-machines"
                className="dock-rise rounded-[calc(var(--radius)*0.8)] bg-background/40 p-4 ring-1 ring-foreground/10"
                style={{ '--i': 2 } as React.CSSProperties}
              >
                <div className="flex items-center justify-between">
                  <p id="cc-machines" className="dock-section-label">Machines</p>
                  <Link
                    href="/machines"
                    onClick={() => onOpenChange(false)}
                    className="inline-flex items-center gap-0.5 text-xs font-medium text-accent hover:text-foreground"
                  >
                    View all
                    <IconChevronRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
                <FleetPreview
                  fleet={fleet}
                  testMode={testMode}
                  onNavigate={() => onOpenChange(false)}
                />
              </section>
            )}

            {/* Navigation tiles */}
            <nav aria-label="Account" className="dock-rise grid grid-cols-2 gap-2.5" style={{ '--i': 3 } as React.CSSProperties}>
              {TILES.map((tile) => {
                const active = isActive(tile.href, tile.exact)
                const Icon = tile.icon
                return (
                  <Link
                    key={tile.href}
                    href={tile.href}
                    onClick={() => onOpenChange(false)}
                    aria-current={active ? 'page' : undefined}
                    className="dock-tile"
                    style={{ '--hue': tile.hue } as React.CSSProperties}
                  >
                    <span className="dock-tile-icon">
                      <Icon className="h-4 w-4" stroke={2} />
                    </span>
                    <span>
                      <span className="block font-heading text-sm font-semibold">{tile.label}</span>
                      <span className="block text-[11px] text-muted-foreground">{tile.caption}</span>
                    </span>
                  </Link>
                )
              })}
            </nav>
          </div>

          {/* Sign out */}
          <div className="relative border-t border-foreground/10 px-5 py-4">
            <button
              type="button"
              onClick={handleSignOut}
              disabled={signingOut || !onSignOut}
              className="dock-eject"
            >
              <IconLogout className="h-4 w-4 text-destructive" stroke={2} />
              {signingOut ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function FleetPreview({
  fleet,
  testMode,
  onNavigate,
}: {
  fleet: FleetState
  testMode: boolean
  onNavigate: () => void
}) {
  if (fleet.kind === 'loading') {
    return (
      <div className="mt-3 space-y-2" aria-label="Loading machines">
        <span className="block h-9 animate-pulse rounded-md bg-foreground/5" />
        <span className="block h-9 animate-pulse rounded-md bg-foreground/5" />
      </div>
    )
  }
  if (fleet.kind !== 'ready') return null

  const { machines } = fleet
  if (machines.length === 0) {
    return (
      <div className="mt-3 flex items-center justify-between gap-3 text-sm">
        <span className="text-muted-foreground">No machines yet.</span>
        <Link href="/products" onClick={onNavigate} className="font-medium text-primary hover:underline">
          Pick a GPU
        </Link>
      </div>
    )
  }

  const counts = [
    { label: 'Running', status: 'running', count: machines.filter((m) => m.status === 'running').length },
    { label: 'Changing', status: 'starting', count: machines.filter((m) => isTransitioningStatus(m.status)).length },
    { label: 'Off', status: 'off', count: machines.filter((m) => m.status === 'off').length },
  ]

  return (
    <>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {counts.map((item) => {
          const config = getMachineStatusConfig(item.status)
          return (
            <div key={item.label} className="rounded-md bg-foreground/[0.04] px-2.5 py-2 ring-1 ring-foreground/8">
              <p className="font-heading text-lg font-bold leading-none tabular-nums">{item.count}</p>
              <p className={cn('mt-1 flex items-center gap-1 text-[10px] font-medium', config.textColor)}>
                <span aria-hidden className={cn('h-1.5 w-1.5 rounded-[1px]', config.dotColor)} />
                {item.label}
              </p>
            </div>
          )
        })}
      </div>

      <ul className="mt-3 space-y-1">
        {machines.slice(0, 3).map((machine) => {
          const config = getMachineStatusConfig(machine.status)
          return (
            <li key={machine.id}>
              <Link
                href={`/machines/${machine.id}`}
                onClick={onNavigate}
                className="flex items-center justify-between gap-3 rounded-md px-2.5 py-2 text-sm transition-colors hover:bg-foreground/5"
              >
                <span className="truncate font-medium">{machine.name}</span>
                <span className={cn('flex shrink-0 items-center gap-1.5 text-xs', config.textColor)}>
                  <span aria-hidden className={cn('h-1.5 w-1.5 rounded-[1px]', config.dotColor, config.pulse && 'animate-pulse')} />
                  {config.label}
                </span>
              </Link>
            </li>
          )
        })}
      </ul>

      {testMode && (
        <p className="mt-3 flex gap-2 rounded-md border border-border bg-card px-2.5 py-2 text-[11px] leading-relaxed text-card-foreground">
          <IconInfoCircle className="mt-px h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          Test mode: these machines are simulated. They are never billed and can&apos;t be connected to.
        </p>
      )}
    </>
  )
}
